import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    schemas: Object,
    labels: Object,
    inputClass: String,
    mode: { type: String, default: "composer" },
    templatesByAccount: Object,
    selectTemplateLabel: String
  }

  static targets = [ "select", "accountSelect", "paramsContainer", "unsupportedMessage", "submitButton" ]

  connect() {
    this.submitting = false
    if (this.isBroadcastMode()) {
      this.filterTemplatesByAccount()
    }
    this.renderParams()
  }

  formSubmitted(event) {
    if (!this.isBroadcastMode()) return

    if (this.submitting) {
      event.preventDefault()
      event.stopImmediatePropagation()
      return
    }

    this.submitting = true
    queueMicrotask(() => this.lockSubmit())
  }

  submitEnded(event) {
    if (!this.isBroadcastMode()) return
    if (event.detail && event.detail.success) return

    this.unlockSubmit()
  }

  accountChanged() {
    if (!this.isBroadcastMode()) return

    this.filterTemplatesByAccount()
    this.renderParams()
  }

  templateChanged() {
    this.renderParams()
  }

  filterTemplatesByAccount() {
    const accountId = this.accountSelectTarget.value
    const select = this.selectTarget

    select.innerHTML = ""
    select.appendChild(this.buildPromptOption(this.selectTemplateLabelValue))

    if (!accountId) {
      select.disabled = true
      return
    }

    const templates = this.templatesByAccountValue[accountId] || []
    templates.forEach((template) => {
      const option = document.createElement("option")
      option.value = template.id
      option.textContent = template.label
      select.appendChild(option)
    })

    select.disabled = templates.length === 0
  }

  buildPromptOption(label) {
    const option = document.createElement("option")
    option.value = ""
    option.textContent = label
    return option
  }

  isBroadcastMode() {
    return this.modeValue === "broadcast" && this.hasAccountSelectTarget && this.hasTemplatesByAccountValue
  }

  renderParams() {
    this.paramsContainerTarget.innerHTML = ""
    this.unsupportedMessageTarget.classList.add("hidden")

    const templateId = this.selectTarget.value
    if (!templateId) {
      this.paramsContainerTarget.classList.add("hidden")
      this.setSubmitDisabled(this.templatesUnavailable())
      return
    }

    const schema = this.schemasValue[templateId]
    if (!schema) {
      this.paramsContainerTarget.classList.add("hidden")
      this.setSubmitDisabled(true)
      return
    }

    if (!schema.sendable) {
      this.paramsContainerTarget.classList.add("hidden")
      this.unsupportedMessageTarget.textContent = this.labelsValue.unsupportedSelected
      this.unsupportedMessageTarget.classList.remove("hidden")
      this.setSubmitDisabled(true)
      return
    }

    if (this.modeValue === "broadcast") {
      this.renderBroadcastParams(schema)
      return
    }

    const fields = []

    if (schema.header.kind === "image") {
      fields.push(this.buildHeaderImageField())
    }

    schema.header.params.forEach((param) => {
      fields.push(this.buildHeaderVariableField(param))
    })

    schema.body_params.forEach((param) => {
      fields.push(this.buildBodyVariableField(param))
    })

    if (fields.length === 0) {
      this.paramsContainerTarget.classList.add("hidden")
    } else {
      this.paramsContainerTarget.classList.remove("hidden")
      fields.forEach((field) => this.paramsContainerTarget.appendChild(field))
    }

    this.setSubmitDisabled(false)
  }

  renderBroadcastParams(schema) {
    const fields = []

    if (schema.csv_columns?.length > 0) {
      fields.push(this.buildExpectedColumnsSection(schema.csv_columns))
    }

    if (schema.header.kind === "image") {
      fields.push(this.buildHeaderImageField())
    }

    if (fields.length === 0) {
      this.paramsContainerTarget.classList.add("hidden")
    } else {
      this.paramsContainerTarget.classList.remove("hidden")
      fields.forEach((field) => this.paramsContainerTarget.appendChild(field))
    }

    this.setSubmitDisabled(false)
  }

  buildExpectedColumnsSection(columns) {
    const section = document.createElement("div")
    section.className = "rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2"

    const heading = document.createElement("p")
    heading.className = "text-sm font-medium text-slate-700"
    heading.textContent = this.labelsValue.expectedColumns

    const list = document.createElement("ul")
    list.className = "list-inside list-disc space-y-0.5 text-sm text-slate-600"

    columns.forEach((column) => {
      const item = document.createElement("li")
      const code = document.createElement("code")
      code.className = "rounded bg-slate-100 px-1 text-xs"
      code.textContent = column
      item.appendChild(code)
      list.appendChild(item)
    })

    section.appendChild(heading)
    section.appendChild(list)

    if (this.labelsValue.expectedColumnsHint) {
      const hint = document.createElement("p")
      hint.className = "text-xs text-slate-500"
      hint.textContent = this.labelsValue.expectedColumnsHint
      section.appendChild(hint)
    }

    return section
  }

  buildBodyVariableField(param) {
    return this.buildTextField({
      name: `variables[${param.key}]`,
      id: `template_body_var_${param.key}`,
      label: this.formatLabel(this.labelsValue.bodyVariable, param.key)
    })
  }

  buildHeaderVariableField(param) {
    return this.buildTextField({
      name: `header_variables[${param.key}]`,
      id: `template_header_var_${param.key}`,
      label: this.formatLabel(this.labelsValue.headerVariable, param.key)
    })
  }

  buildHeaderImageField() {
    const name = this.isBroadcastMode() ? "whatsapp_broadcast[header_image]" : "header_image"

    return this.buildFileField({
      name,
      id: "template_header_image",
      label: this.labelsValue.headerImageUpload,
      accept: "image/*",
      required: true
    })
  }

  buildFileField({ name, id, label, accept, required = false }) {
    const wrapper = document.createElement("div")

    const labelEl = document.createElement("label")
    labelEl.htmlFor = id
    labelEl.textContent = label

    const input = document.createElement("input")
    input.type = "file"
    input.name = name
    input.id = id
    input.accept = accept
    if (required) input.required = true

    if (this.isBroadcastMode()) {
      labelEl.className = "block text-sm font-medium text-slate-700 mb-1"
      input.className = "block w-full text-sm text-slate-600 file:mr-4 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-slate-700 hover:file:bg-slate-200"

      const fileWrapper = document.createElement("div")
      fileWrapper.className = "w-full border border-slate-300 rounded-lg px-3 py-2 focus-within:ring-2 focus-within:ring-blue-500"
      fileWrapper.appendChild(input)

      wrapper.appendChild(labelEl)
      wrapper.appendChild(fileWrapper)
    } else {
      labelEl.className = "block text-xs font-medium text-[#667781]"
      input.className = this.inputClassValue

      wrapper.className = "space-y-1"
      wrapper.appendChild(labelEl)
      wrapper.appendChild(input)
    }

    return wrapper
  }

  buildTextField({ name, id, label, inputType = "text", required = true }) {
    const wrapper = document.createElement("div")
    wrapper.className = "space-y-1"

    const labelEl = document.createElement("label")
    labelEl.className = "block text-xs font-medium text-[#667781]"
    labelEl.htmlFor = id
    labelEl.textContent = label

    const input = document.createElement("input")
    input.type = inputType
    input.name = name
    input.id = id
    if (required) input.required = true
    input.className = this.inputClassValue

    wrapper.appendChild(labelEl)
    wrapper.appendChild(input)
    return wrapper
  }

  formatLabel(template, key) {
    return String(template).replace("{{key}}", key)
  }

  setSubmitDisabled(disabled) {
    if (!this.hasSubmitButtonTarget) return
    if (this.submitting) {
      this.submitButtonTarget.disabled = true
      return
    }

    this.submitButtonTarget.disabled = disabled
  }

  lockSubmit() {
    if (!this.hasSubmitButtonTarget) return

    const submit = this.submitButtonTarget
    if (!submit.dataset.originalLabel) submit.dataset.originalLabel = submit.value
    submit.disabled = true
    submit.setAttribute("aria-busy", "true")
    submit.value = submit.getAttribute("data-turbo-submits-with") || submit.value
  }

  unlockSubmit() {
    this.submitting = false
    if (!this.hasSubmitButtonTarget) return

    const submit = this.submitButtonTarget
    submit.removeAttribute("aria-busy")
    if (submit.dataset.originalLabel) submit.value = submit.dataset.originalLabel
    this.setSubmitDisabled(this.templatesUnavailable() || !this.selectTarget.value)
  }

  templatesUnavailable() {
    if (this.isBroadcastMode()) {
      const accountId = this.accountSelectTarget.value
      if (!accountId) return true

      const templates = this.templatesByAccountValue[accountId] || []
      return templates.length === 0
    }

    return this.selectTarget.options.length <= 1
  }
}
